import { clamp, hash01, hash3, mulberry32 } from '@/core/rng';
import type { BandActivity, PersonState } from './types';

/**
 * People (v0.3.2 §3–§6). A band of twenty-five holds twenty-five persistent
 * identities without running twenty-five simulations: parties do the walking,
 * and these are the lives inside them. Someone curious keeps turning up on
 * scouting trips; someone with a hunter's hands keeps going after the herd.
 * Nobody has a job title, and nobody levels up.
 *
 * Everything here is a pure function of (band identity, person index, world
 * seed), so the same world names the same people and gives them the same
 * hands, on every machine and after every reload.
 */

// A small syllable set. It only needs to produce names a player can tell
// apart and remember — Ena, Tor, Mara — not a language.
const HEAD = ['A', 'E', 'I', 'O', 'U', 'Ka', 'Ma', 'Ta', 'Na', 'Sa', 'Ve', 'Ro',
              'Li', 'Ha', 'Da', 'Ne', 'Yu', 'Bo', 'Fe', 'Gi'] as const;
const TAIL = ['na', 'ra', 'ni', 'sk', 'th', 'la', 'ren', 'mar', 'vek', 'sha',
              'ko', 'del', 'wen', 'tas', 'ir', 'or', 'em', 'ul', 'an', 'ys'] as const;

/** A name from a hash — two or three syllables, capitalised. */
export function personName(seed: number, idx: number): string {
  const a = HEAD[hash3(seed, idx, 0x4e1) % HEAD.length];
  const b = TAIL[hash3(seed, idx, 0x4e2) % TAIL.length];
  // roughly a third of people get a third syllable, so names vary in weight
  const long = hash01(seed, idx, 0x4e3) < 0.34;
  const c = long ? TAIL[hash3(seed, idx, 0x4e4) % TAIL.length] : '';
  return a + b + c;
}

const YEAR = 365 * 86400;

/**
 * The age pyramid a band is founded with.
 *
 * A band used to be founded as twenty-five people all born on the same day,
 * which `tools/graves.ts` caught the moment it started reading `bornAt`: after
 * twenty-five years the island's median age was **8.1 years**, nobody at all
 * was over thirty, and the oldest people alive were the founders, at exactly
 * the age of the world. That is not a community of foragers, it is a nursery —
 * and it is a demographic pulse besides, since a cohort born on one day ages,
 * and would die, in lockstep.
 *
 * So a founding band gets a real pyramid. These are the shares of a
 * high-mortality forager population, the shape a stationary population takes
 * under a life table where about four in ten never reach fifteen and those who
 * do can expect another three or four decades: broad at the base, thinning
 * steadily, a few elders. Sampled by inverse CDF from the person's own hash,
 * so it stays a pure function of (band, index, seed).
 *
 * It is not a claim about *this* world's mortality — that is
 * `sim/mortals/mortality.ts`' business. It is the answer to "what does a band
 * look like on the day the world starts", which has to be something, and was
 * previously "everyone is a newborn".
 */
const PYRAMID: ReadonlyArray<readonly [number, number]> = [
  // [age at the top of the band, cumulative share below it]
  [5, 0.19], [15, 0.41], [30, 0.66], [45, 0.84], [60, 0.95], [75, 1.00],
];

/** A founding age in years, sampled from the pyramid by inverse CDF. */
export function foundingAge(bandSeed: number, idx: number): number {
  const u = hash01(bandSeed, idx, 0x51fe);
  let lo = 0, cum = 0;
  for (const [top, share] of PYRAMID) {
    if (u < share) {
      const within = (u - cum) / Math.max(1e-9, share - cum);
      return lo + within * (top - lo);
    }
    lo = top; cum = share;
  }
  return 75;
}

/**
 * When a founding member was born, given the world time the band is founded.
 * Negative world times are fine: `bornAt` is only ever read as a difference.
 */
export const foundingBornAt = (bandSeed: number, idx: number, now: number): number =>
  now - foundingAge(bandSeed, idx) * YEAR;

/**
 * Make one person. `idx` is their index within the band's whole history, so
 * the tenth person a band ever has is always the same person.
 */
export function makePerson(
  bandId: string, bandSeed: number, idx: number, bornAt: number,
): PersonState {
  const rng = mulberry32(hash3(bandSeed, idx, 0x9e17));
  const p: PersonState = {
    id: `${bandId}-h${idx}`,
    name: personName(bandSeed, idx),
    // born at full health; the young and the very old are weaker, later
    vitality: 0.82 + rng() * 0.18,
    bornAt,
    traits: {
      curiosity: 0.15 + rng() * 0.7,
      courage: 0.15 + rng() * 0.7,
      sociability: 0.15 + rng() * 0.7,
    },
    aptitudes: {
      hunt: 0.2 + rng() * 0.6,
      forage: 0.2 + rng() * 0.6,
      build: 0.2 + rng() * 0.6,
      explore: 0.2 + rng() * 0.6,
    },
    importance: 0,
    memories: [],
  };
  recordBorn(p);
  return p;
}

/**
 * Who somebody was born as — the seven axes at birth, kept so a life has
 * its own ceiling (`ceilingOf`). Written once by `makePerson` and again by
 * `makeChild` after inheritance has been applied; never afterwards.
 */
const recordBorn = (p: PersonState): void => {
  const born = {} as Record<TraitAxis, number>;
  for (const k of TRAIT_AXES) born[k] = axisOf(p, k);
  p.born = born;
};

/**
 * The range a life may occupy, wider than the range a life may BEGIN in
 * (0.15..0.85 for temperament, 0.2..0.8 for a craft, below).
 *
 * A person who spends forty years at one work must be able to end up outside
 * the range anybody is born into — that is what a life doing something to
 * you means — but not at 0 or 1, because a saturated axis is a person the
 * world can no longer say anything new about. `culture.ts`'s header records
 * the mirror of this trap: clamp floors at 0.05/0.95 stalled band-trait
 * divergence at exactly 0.20 after forty-five years, because a random walk
 * against a wall stops being a walk. These are not walls a walk hits; they
 * are the asymptote of a move-toward, which can approach and never arrive.
 */
const LIFE_MIN = 0.03, LIFE_MAX = 0.97;

/**
 * THE LIFE MODEL'S KNOBS, in one place and swappable, so a probe can run the
 * same functions under a candidate and report the century it makes —
 * `tools/saturation.ts` does, and it is how `TRANSFORM` and `TOWARD_NORM`
 * were chosen rather than guessed. The simulation always runs the shipped
 * values; a caller that passes nothing gets exactly them.
 */
export interface LifeModel {
  /**
   * HOW MUCH OF THE WAY TO THE EXTREME A LIFE OF WORK CAN CARRY AN AXIS.
   *
   * A life used to harden an asked axis toward `LIFE_MAX` itself, every
   * working day, with nothing opposing it — so over a century every hunter
   * converged on the same hunter (ROADMAP §46.4: the median asked axis
   * within 0.06 of the clamp; TRAITS.md: 57% of hunt axes pinned, "bold" on
   * 95% of cards). Two sinks and no source, and a move-toward a COMMON
   * target is a sink whatever the target is — lowering `LIFE_MAX` or adding
   * an opposing drift only moves where everybody meets.
   *
   * So the target is the person's own: `ceilingOf` is where THEY were born
   * plus this fraction of the distance to the extreme. A life is a real but
   * never total transformation — which `HARDEN`'s own note has claimed since
   * it was written, and which is now true at infinite time rather than only
   * at short ones — and the spread people are born with is the spread they
   * keep. A god's press is not bound by it (`press` still moves toward
   * `LIFE_MAX`): a hand can carry a life past what its own work could, and
   * the life then settles back toward its own nature over the years, which
   * is law 19's dependence and law 25's decay arriving by the same road.
   */
  transform: number;
  /** How far a newborn regresses from its parent toward the band's middle. */
  towardNorm: number;
  /** Half-width of the variation every birth adds on top. */
  variation: number;
  /**
   * WHERE DISUSE TAKES AN AXIS. `norm` is the band's middle — the shipped rule
   * since DESIGN-VI — and it is a sink: one day of foraging sets
   * `shown.curiosity` for life, and every day after that the work does not
   * ask for it pulls the axis toward the same shared value, so a people's
   * curiosity contracts for a century and "curious" leaves the card (the
   * first regeneration of 2026-08-22 measured it: p95 0.65, sd 0.070, nobody
   * on the island curious). `born` is the person's own: losing a practice
   * takes you back to who you were, not to the band's average — which is
   * what the rule's own note has always said it meant ("disuse is losing a
   * practice, not losing a disposition you never had").
   */
  relaxToward: 'norm' | 'born';
}

/**
 * The shipped values. `transform` and `towardNorm` were chosen by
 * `tools/saturation.ts` — a synthetic century of the real `liveADay`,
 * `makeChild` and `died` over a band of twenty-five, swept over both — for
 * the century that keeps a people various: within-band spread held near
 * what they are born with, a single card word on no more than a third of
 * veterans, and room left for a god's press on the median working axis.
 */
export const LIFE_MODEL: LifeModel = {
  transform: 0.5, towardNorm: 0.25, variation: 0.18, relaxToward: 'born',
};

/**
 * Where a life of work can carry this axis: born, plus `transform` of the
 * way to the extreme. Somebody born without a record (a save from before
 * lives had one) is taken as born where they stand.
 */
export function ceilingOf(p: PersonState, k: TraitAxis, model: LifeModel = LIFE_MODEL): number {
  const born = p.born?.[k] ?? axisOf(p, k);
  return clamp(born + (LIFE_MAX - born) * model.transform, LIFE_MIN, LIFE_MAX);
}

/**
 * The seven numbers a person is, named as one vocabulary.
 *
 * Three temperaments and four aptitudes, because `suitability` below is the
 * whole reader and it reads exactly these. There is no eighth: a tendency
 * exists only where a system already resolves something it can bias
 * (`DESIGN-VI.md` Rule 1), and nothing in this world resolves anything by
 * "aggression" or "patience".
 */
export type TraitAxis =
  | 'curiosity' | 'courage' | 'sociability'
  | 'hunt' | 'forage' | 'build' | 'explore';

/** The axes in a fixed order. Iteration order is determinism. */
export const TRAIT_AXES: readonly TraitAxis[] =
  ['curiosity', 'courage', 'sociability', 'hunt', 'forage', 'build', 'explore'];

const TEMPERAMENTS: ReadonlySet<string> = new Set(['curiosity', 'courage', 'sociability']);

/**
 * THE LINES THE CARD SPEAKS AT. Above `STANDS_OUT` a neighbour would call
 * somebody *bold*, *curious*, *well liked*, or say they have *a hunter's
 * hands*; below `STANDS_APART`, *wary*, *settled*, *keeps apart*. These were
 * `ui/lives.ts`'s own thresholds and are kept here so the simulation and the
 * card cannot drift: a crossing the world records (`person_changed`) is
 * exactly a change in what the card would say.
 */
export const STANDS_OUT = 0.66;
export const STANDS_APART = 0.34;

/** Read one axis, whichever half of the person it lives on. */
export const axisOf = (p: PersonState, k: TraitAxis): number =>
  TEMPERAMENTS.has(k)
    ? p.traits[k as 'curiosity' | 'courage' | 'sociability']
    : p.aptitudes[k as 'hunt' | 'forage' | 'build' | 'explore'];

/** Write one axis. Clamped to the range a life may occupy, never to 0 or 1. */
const setAxis = (p: PersonState, k: TraitAxis, v: number): void => {
  const c = clamp(v, LIFE_MIN, LIFE_MAX);
  if (TEMPERAMENTS.has(k)) p.traits[k as 'curiosity' | 'courage' | 'sociability'] = c;
  else p.aptitudes[k as 'hunt' | 'forage' | 'build' | 'explore'] = c;
};

/**
 * WHAT EACH WORK ASKS OF A PERSON — and the only copy of it.
 *
 * `suitability` reads this to choose who goes, and `liveADay` reads the same
 * rows to decide what a day of that work makes of them. That is the whole of
 * `DESIGN-VI.md` §3.2's first rule: the reader IS the mapping, so a day's work
 * can only ever shape the tendencies that chose the person for it, and no
 * second activity→trait table can drift out of step with the first. The
 * repository has paid three times for a hand-mirrored expression (the fuel
 * term lives in three files and `world/strain.ts` says so out loud); this one
 * does not get to be the fourth.
 *
 * The weights and the per-activity bases below are `suitability`'s own,
 * unchanged: summed in this order they reproduce the previous expression to
 * the bit, which `test/lives.ts` asserts against a hand-written copy of the
 * old arithmetic rather than trusting this sentence.
 */
const WORK: Partial<Record<BandActivity, ReadonlyArray<readonly [TraitAxis, number]>>> = {
  hunt: [['hunt', 0.7], ['courage', 0.3]],
  forage: [['forage', 0.8], ['curiosity', 0.1]],
  gather_wood: [['build', 0.4], ['forage', 0.4]],
  build: [['build', 0.9]],
  explore: [['explore', 0.55], ['curiosity', 0.35], ['courage', 0.1]],
  // who walks toward a thing nobody can explain: the curious, and the brave
  investigate: [['curiosity', 0.5], ['courage', 0.3], ['explore', 0.2]],
  // who goes back to a holy place: whoever is most bound to the others
  venerate: [['sociability', 0.6]],
  seek_water: [['forage', 0.5]],
};

/** The constant each work adds on top of its weighted axes. */
const WORK_BASE: Partial<Record<BandActivity, number>> = {
  venerate: 0.2, seek_water: 0.2,
};

/** Fleeing and travelling take everyone; camp is where the rest are. */
const NO_WORK = 0.5;

/**
 * THE AGE BELOW WHICH A WORK DOES NOT CHOOSE SOMEBODY — when it has a
 * choice. Measured before it existed (2026-08-23, the shipped century):
 * **28% of every hunting party was under ten and thirty-five hunters were
 * under five**, because `assignPeople` ranked by suitability alone and a
 * newborn's rolled aptitude is as rankable as anyone's. And a hard gate
 * would be wrong by the same measurement: bands want more hands out than
 * they hold of working age (band-5: 35 wanted, 26 twelve-or-over), so the
 * young must still be assignable. The rule is therefore a sort, not a wall:
 * somebody under the work's age scores beneath every grown candidate and
 * fills only the remainder — and the fill order (`rank`) already hands the
 * demanding work its pick first, so the children who do go out go to the
 * gathering and the foraging, which is where children go. The yield is
 * untouched by construction: a party's work is priced by its SIZE, never by
 * who is in it.
 */
const OF_AGE: Partial<Record<BandActivity, number>> = {
  hunt: 12, explore: 12, investigate: 12,
  gather_wood: 8, build: 8, seek_water: 6, forage: 4, venerate: 4,
};
const YOUNG_LAST = 1;

/** What a work asks, or nothing — the shared read for both callers. */
export const asked = (
  activity: BandActivity,
): ReadonlyArray<readonly [TraitAxis, number]> => WORK[activity] ?? [];

/**
 * How well this person suits that work. Aptitude leads, temperament tilts:
 * the curious volunteer to scout, the brave are readier to face a herd or a
 * predator, and anybody in poor condition is worth less on a long day.
 * Deliberately soft — nobody is disqualified from anything.
 */
export function suitability(p: PersonState, activity: BandActivity): number {
  const rows = WORK[activity];
  let s: number;
  if (!rows) s = NO_WORK;
  else {
    s = 0;
    for (const [k, w] of rows) s += axisOf(p, k) * w;
    s += WORK_BASE[activity] ?? 0;
  }
  return clamp(s, 0, 1) * (0.55 + 0.45 * clamp(p.vitality, 0, 1));
}

/**
 * Hand the band's people out to its parties (v0.3.2 §6).
 *
 * Parties already know how many they need — that is the band's strategic
 * decision — and this decides who goes. Each party is filled in turn by
 * suitability, with a deterministic tie-break, so the same band always sends
 * the same people and a person's history is their own repeated behaviour
 * rather than a die roll. Whoever is left keeps the fire.
 */
export function assignPeople(
  people: PersonState[],
  parties: Array<{ id: string; activity: BandActivity; size: number }>,
  bandSeed: number,
  decisionIndex: number,
  /**
   * World-seconds now, for the working ages (`OF_AGE`). Optional so a
   * caller that predates the rule keeps its old sort; the band passes it.
   */
  now?: number,
): void {
  for (const p of people) delete p.partyId;
  const unclaimed = new Set(people);
  // fill the demanding work first: a hunt wants its best hands before the
  // camp roster takes whoever is nearest the fire
  const order = [...parties].sort((a, b) => rank(b.activity) - rank(a.activity));
  for (const party of order) {
    const want = Math.min(party.size, unclaimed.size);
    if (want <= 0) continue;
    const ranked = [...unclaimed].sort((x, y) => {
      const d = score(y, party.activity, bandSeed, decisionIndex, now)
              - score(x, party.activity, bandSeed, decisionIndex, now);
      return d !== 0 ? d : (x.id < y.id ? -1 : 1);
    });
    for (let i = 0; i < want; i++) {
      ranked[i].partyId = party.id;
      unclaimed.delete(ranked[i]);
    }
  }
}

/** Suitability plus a whisper of noise, so a band is not perfectly sorted —
 *  and the young sorted beneath every grown candidate for a work that is
 *  not theirs yet (`OF_AGE`), without ever being unassignable. */
const score = (
  p: PersonState, a: BandActivity, bandSeed: number, decisionIndex: number, now?: number,
): number => {
  const base = suitability(p, a) +
    (hash01(hash3(bandSeed, decisionIndex, 0x51), p.id.length + a.length, 0x52) - 0.5) * 0.12;
  const age = OF_AGE[a];
  if (now === undefined || age === undefined) return base;
  return (now - p.bornAt) / LIFE_YEAR < age ? base - YOUNG_LAST : base;
};

/** Which work gets first pick of the band. */
const rank = (a: BandActivity): number =>
  a === 'hunt' ? 5 : a === 'investigate' ? 5 : a === 'explore' ? 4 : a === 'build' ? 3
  : a === 'gather_wood' ? 2 : a === 'seek_water' ? 2 : a === 'forage' ? 1 : 0;

// ---------------------------------------------------------------------------
// a life, and what it does to a person — DESIGN-VI §4.1
// ---------------------------------------------------------------------------

/**
 * ## Nothing about a person used to change, ever
 *
 * Before this, `PersonState`'s seven numbers were rolled once in `makePerson`
 * and never written again — not by age, not by hardship, not by forty years of
 * doing one thing. The type's own comments promised otherwise ("falls with
 * hardship and, late on, with age"; "high courage may get someone killed") and
 * described nothing that ran. `tools/imprint-life.ts §3` prints the baseline
 * it replaces: **|Δcuriosity| over a whole life, p10 through max, all 0.000.**
 *
 * ## What the measurement licensed
 *
 * The rule below only means anything if the same person keeps turning up on
 * the same work, and `people.ts`'s header has claimed that since it was
 * written without anyone checking. `tools/imprint-life.ts §1` checked, over
 * eight god-free world-years: **the share of a person's out-of-camp days spent
 * on the single work they do most is p50 0.968** (p10 0.584), across a median
 * of 3 distinct works. So a forager is a forager, and a day of foraging is a
 * fact about them rather than a coin flip.
 *
 * The other reading is the one every constant here is sized against:
 * **a person accumulates p50 225 out-of-camp days a world-year** (§6), so a
 * forty-year working life is ~9,000 of them. Any rule that adds a fixed
 * quantity per day saturates instantly at that count; the only shape that
 * survives it is a MOVE TOWARD — which is also law 25 on the subject's own
 * side, and the shape `strainField.press` chose for the same reason.
 */

/**
 * How fast a day of work moves the axes that work asked for, at full
 * plasticity, as a fraction of the distance remaining to the extreme.
 *
 * **SIZED, then VERIFIED — not swept.** Sized from `tools/imprint-life.ts §6`:
 * a person accumulates p50 **225 out-of-camp days a world-year**, so at half
 * plasticity this closes about a tenth of the remaining distance per year of
 * steady work, and a working life is a real but never total transformation.
 * Verified against §7's four readings over twenty-four god-free world-years,
 * all of which have a silent failure mode and none of which is the others: a
 * life MOVES (largest |Δaxis| p50 0.093, against a baseline of exactly 0.000),
 * the spread inside a band does NOT collapse (per-axis SD 0.069–0.214), no
 * axis SATURATES (2 of 770 within 0.06 of a clamp), and the work
 * DIFFERENTIATES — hunters carry **+0.224 courage** over the rest of their
 * own band, which is a fact about their lives and not about their birth.
 *
 * No sweep was run, so this is a defensible value rather than an optimum;
 * `test/lives.ts` L2–L3 is what stops it drifting into either failure.
 *
 * ⚠ **THE "NO AXIS SATURATES" READING DOES NOT HOLD ON THE SHIPPED CENTURY,
 * AND THE TWO WERE MEASURED ON DIFFERENT WORLDS.** That 2-of-770 figure comes
 * from a world grown for the test's own span. Re-measured against
 * `public/prehistory.json` — 100 world-years, 464 souls — by `tools/press.ts`:
 *
 *     headroom (LIFE_MAX − v) on the axes a person's work asks for
 *         p10 0.008      p50 0.055      p90 0.249
 *
 * So the MEDIAN asked axis is within 0.06 of the clamp, which is the very
 * condition the short-run reading reported as almost absent, and the top
 * decile is on it. The mechanism is plain once looked at: `liveADay` hardens
 * an asked axis toward `LIFE_MAX` every working day, and `RELAX` is guarded to
 * axes with `shown > 0` that today did NOT ask — so an axis somebody uses for
 * a lifetime has a ratchet and no opposing force. Over a century the hunters
 * converge on the same hunter.
 *
 * That was a real defect in the life model and it was deliberately NOT fixed
 * in the commit that found it: the candidates (an opposing drift on asked
 * axes, a lower `LIFE_MAX`, a `RELAX` that runs everywhere) all change what
 * every person in the shipped century IS, which is a design call and a
 * regeneration. **Fixed 2026-08-22 by ruling** (`DECISIONS.md`, the First
 * Playable lane's rulings), and not by any of those three: a move-toward a
 * COMMON target is a sink whatever the target is, so the target became the
 * person's own — `ceilingOf`, born plus `LifeModel.transform` of the way to
 * the extreme. `tools/saturation.ts` chose the fraction on a synthetic
 * century under a hungry band's party mix (the mix that reproduces the
 * defect's direction — hunt 35% pinned under the shipped model): at 0.5,
 * hunt pinned 35% → 4%, courage 11% → 3%, within-band sd held, the card's
 * commonest phrase 25% → 23% of a roster and sixteen phrases for twenty-five
 * people, and a god's press with twice the room (headroom p50 0.116 →
 * 0.228). The regeneration that followed paid it. **Do not "fix"
 * `PRESS_DAYS` to compensate for anything** — that was tried first and it is
 * the wrong lever, see there.
 */
const HARDEN = 0.0010;

/** How fast an axis nobody asked for today drifts back toward the band's own
 *  middle. Runs every day, not only working ones, and is deliberately well
 *  under `HARDEN`: a person is shaped more by what they do than by what they
 *  do not. Same provenance as `HARDEN`, and the reading it must not break is
 *  the within-band spread — 0.19 before any of this existed, and neither to
 *  collapse (everyone becomes the average) nor to saturate (everyone reaches
 *  an extreme).
 *
 *  **The weakest axis is `sociability`, and the reason is not this number.**
 *  It settles near 0.069 where the others hold 0.10–0.21, because the only
 *  work that asks for it is `venerate`, and a god-free island has no sacred
 *  place to walk back to — so no day of anybody's life ever asks. The fix is
 *  a reader, not a constant: an axis the world never exercises is one the
 *  world has no opinion about. */
const RELAX = 0.00025;

/** Plasticity's floor: an elder still moves, barely. Never zero — a wall
 *  teaches nothing, which is `VIABILITY_FLOOR`'s argument one system over. */
const PLASTIC_FLOOR = 0.08;
/** Years at which the age term has fallen halfway to that floor. */
const PLASTIC_HALF = 18;
/** How sharply it falls. 2 gives a long childhood and a slow close. */
const PLASTIC_EXP = 2;
/** Expressed days at which experience alone has halved plasticity: a person
 *  is shaped most by their first seasons of a thing. ~6.7 years at §6's p50. */
const HARD_SCALE = 1500;

const LIFE_YEAR = 365 * 86400;

/**
 * How far this person can still be moved on this axis, 0..1.
 *
 * Two terms, both from the person's own history and neither from the clock:
 * how old they are, and how much of this particular axis they have already
 * spent living. `DESIGN-VI.md` Rule 4 — plasticity is a state, not a constant,
 * and the same function answers a newborn and an elder.
 */
export function plasticityOf(p: PersonState, k: TraitAxis, now: number): number {
  const days = p.shown?.[k] ?? 0;
  return agePlasticity(p, now) / (1 + days / HARD_SCALE);
}

/**
 * The AGE half of plasticity, alone — a life closing, with nothing about what
 * this particular person has practised.
 *
 * Split out rather than inlined because the two terms answer different
 * questions, and one caller needs only the first:
 *
 *  · **Age** is a limit on what anybody can still become. It binds a god
 *    exactly as it binds a life, which is why `press` runs through it.
 *  · **Experience** (`1 + shown/HARD_SCALE`) is *learning saturation* — the
 *    thousandth day of foraging teaches less than the first. It is a statement
 *    about what a DAY OF WORK can teach, and a god is not teaching.
 *
 * **This split is a ruling, and it was forced by a measurement** (`tools/press.ts`).
 * Strengthen may only move the axes the person's work is currently asking for
 * — that is the whole design, and `DECISIONS.md` keeps it. But on the shipped
 * century those are precisely the axes with the most `shown`: median 1,990
 * days against a `HARD_SCALE` of 1,500, so the experience term alone divides
 * their plasticity by 2.3 at the median and 6.1 at p90. Measured end to end:
 * plasticity on asked axes p50 **0.164**, on every other axis p50 **0.329** —
 * exactly half. The rule "reinforce what is expressed" was therefore aiming a
 * god at the one part of a person that had hardened most against being moved,
 * and the median cast moved a life by **0.000**.
 *
 * So the collision is real and it is resolved here rather than by inflating a
 * rate until it papers over: a god presses against age, not against expertise.
 * Law 25 is untouched and does not need the experience term to hold — the
 * headroom factor `(LIFE_MAX − v)` shrinks with every press, so repetition on
 * one person still decays geometrically toward nothing on its own.
 */
export function agePlasticity(p: PersonState, now: number): number {
  const age = Math.max(0, (now - p.bornAt) / LIFE_YEAR);
  return PLASTIC_FLOOR
    + (1 - PLASTIC_FLOOR) / (1 + Math.pow(age / PLASTIC_HALF, PLASTIC_EXP));
}

/** The roster's own middle on every axis — what "ordinary, here" means. */
export function rosterNorm(people: readonly PersonState[]): Record<TraitAxis, number> {
  const out = {} as Record<TraitAxis, number>;
  for (const k of TRAIT_AXES) {
    if (people.length === 0) { out[k] = 0.5; continue; }
    let s = 0;
    for (const p of people) s += axisOf(p, k);
    out[k] = s / people.length;
  }
  return out;
}

/**
 * One day of being alive, for every person in a band.
 *
 * **Expression hardens.** Whatever work a person is out on today asked
 * something of them — `asked(activity)` is the very weight vector that chose
 * them for it — and each axis it asked for moves that fraction of the way
 * toward its extreme. Nobody levels up and nobody gains a title: the hunter
 * keeps being sent to hunt, and becomes more the person who is sent.
 *
 * **Disuse relaxes.** Every axis today did not ask for drifts toward the
 * roster's middle. This is what stops the band becoming seven saturated
 * extremes, and it is why a curious child kept at the hearth grows ordinary.
 *
 * **Age closes.** Both rates run through `plasticityOf`, so a life shapes its
 * first seasons hardest and an elder is very nearly what a people remembers
 * being.
 *
 * Order-independent within the day by construction: the norm is taken from
 * the roster BEFORE any of it is written, so who is processed first cannot
 * change what anybody becomes. Called once per absolute day boundary, so
 * `advance(a,b) + advance(b,c)` is `advance(a,c)` here as everywhere (law 7).
 */
export function liveADay(
  people: PersonState[],
  activityOf: (p: PersonState) => BandActivity | undefined,
  now: number,
  model: LifeModel = LIFE_MODEL,
): void {
  if (people.length === 0) return;
  const norm = rosterNorm(people);
  for (const p of people) {
    const activity = activityOf(p);
    const rows = activity ? asked(activity) : [];
    // what today asked of this person, by axis
    const want = new Map<TraitAxis, number>();
    for (const [k, w] of rows) want.set(k, (want.get(k) ?? 0) + w);
    for (const k of TRAIT_AXES) {
      const plastic = plasticityOf(p, k, now);
      const w = want.get(k);
      const v = axisOf(p, k);
      if (w !== undefined && w > 0) {
        // toward THIS life's ceiling, in proportion to how much the work
        // leaned on it — and back down toward it, at the same rate, from
        // wherever a god's hand left it above (`LifeModel.transform`)
        setAxis(p, k, v + (ceilingOf(p, k, model) - v) * HARDEN * w * plastic);
        const shown = p.shown ?? (p.shown = {});
        shown[k] = (shown[k] ?? 0) + w;
      } else if ((p.shown?.[k] ?? 0) > 0) {
        // DISUSE IS LOSING A PRACTICE, NOT LOSING A DISPOSITION YOU NEVER HAD.
        //
        // The `shown > 0` guard is not a nicety, it is what stops the rule
        // being a variance sink. Relaxation toward a shared middle is a
        // contraction: run it on an axis nobody is ever ASKED for and the
        // whole band converges on one value for it, with nothing ever pushing
        // back. `tools/imprint-life.ts §7` caught exactly that over
        // twenty-four world-years, on the three axes this island rarely
        // musters — sociability fell from a spread of 0.192 to **0.045**,
        // because `venerate` needs a sacred place and a god-free island has
        // none, so no day of anybody's life ever asked for it. Curiosity and
        // the two working aptitudes, which the world asks for constantly,
        // held far better.
        //
        // A person who has never built has no builder's practice to fall out
        // of, and stays whatever they were born; a person who built for ten
        // years and stopped does. That is both the honest reading of the word
        // and the thing that keeps a people various.
        const back = model.relaxToward === 'born' ? (p.born?.[k] ?? norm[k]) : norm[k];
        setAxis(p, k, v + (back - v) * RELAX * plastic);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// who you keep ending up beside — the first relationship that is not lineage
// ---------------------------------------------------------------------------

/** How many people one person can be said to keep company with. Small on
 *  purpose: a person close to everybody is close to nobody, and the card has
 *  room to name one or two. Same shape as the memory cap, same argument. */
const BOND_CAP = 6;
/** Per-day forgetting, applied to every bond whether or not it was renewed.
 *  A companionship is a thing you keep doing; stop and it fades. At 0.999 a
 *  bond left alone halves in ~1.9 world-years, so a season together survives a
 *  winter apart and a single shared errand does not survive the month. */
const BOND_FADE = 0.999;
/** Below this a bond is dropped outright rather than lingering at a hair's
 *  weight for ever — `EnvField`'s NEGLIGIBLE, one system over. */
const BOND_FLOOR = 0.5;
/** Days together before the card will say anything about it at all. */
export const BOND_SPEAKS = 24;
/**
 * How far the strongest companion must stand clear of the middle of the same
 * person's circle before the card will NAME them. **MEASURED**,
 * `tools/bonds.ts` §3, and it exists because the first version was a lie.
 *
 * A band re-draws its parties from the same two dozen people every planning
 * cycle, so everybody accumulates days with everybody: over twelve world-years
 * the strongest bond was only **p50 1.3×** the median one (p10 1.1×, p90 2.1×).
 * A list like that is an ATTENDANCE REGISTER, and "often ranges out with Mara"
 * said of an attendance register is the card telling the player something the
 * world does not know — the exact failure `test/follow.ts` exists to prevent,
 * arriving as prose instead of as a number.
 *
 * So the card speaks only when somebody genuinely stands out. Most people have
 * no named companion and that is the truthful answer: they work with whoever
 * the day assigns. When the card does name somebody it means it.
 */
const BOND_STANDS_OUT = 1.7;

/**
 * The one person this card may name, or nothing.
 *
 * Two gates, and both must pass: long enough to matter (`BOND_SPEAKS`) and
 * clearly more than the rest of the circle (`BOND_STANDS_OUT`). Someone with
 * fewer than three companions is compared against what they have; someone with
 * one companion and a long history has, self-evidently, a companion.
 */
export function companionOf(p: PersonState): { id: string; days: number } | undefined {
  const c = companionsOf(p);
  if (c.length === 0 || c[0].days < BOND_SPEAKS) return undefined;
  if (c.length === 1) return c[0];
  const mid = c[Math.floor(c.length / 2)].days;
  return mid <= 0 || c[0].days / mid >= BOND_STANDS_OUT ? c[0] : undefined;
}

/**
 * A day's work, recorded as company.
 *
 * Called once per absolute day boundary, per party, with that party's members
 * in roster order. Two people on the same working party today each gain a day
 * of each other; everyone's whole list fades a little whether they worked or
 * not.
 *
 * **The gate is `asked(activity)`, which is deliberately the same gate
 * `liveADay` and `press` use.** Camp asks nothing and neither does fleeing or
 * travelling, so neither builds a bond — and that is the honest reading rather
 * than a saving: sitting by the same fire as thirty other people is not
 * company, and running from the same fire is not either. What counts is having
 * chosen to spend the day on the same errand. One mapping, three consumers,
 * and no second table to drift out of step.
 *
 * Order-independent: the pair loop is `i < j` over a fixed roster order and
 * each pair is written symmetrically, so who is processed first cannot change
 * anybody's list.
 */
export function shareADay(party: readonly PersonState[], activity: BandActivity): void {
  if (party.length < 2 || asked(activity).length === 0) return;
  for (let i = 0; i < party.length; i++) {
    for (let j = i + 1; j < party.length; j++) {
      bump(party[i], party[j].id);
      bump(party[j], party[i].id);
    }
  }
}

/** One more day of somebody, with the weakest evicted if the list is full. */
function bump(p: PersonState, other: string): void {
  const list = p.bonds ?? (p.bonds = []);
  const at = list.findIndex((b) => b.id === other);
  if (at >= 0) { list[at].days += 1; return; }
  if (list.length < BOND_CAP) { list.push({ id: other, days: 1 }); return; }
  // full: the faintest goes, and only if it is fainter than a fresh day is
  // worth — otherwise a stable set of companions would be churned by every
  // stranger who turned up on one errand
  let weakest = 0;
  for (let i = 1; i < list.length; i++) if (list[i].days < list[weakest].days) weakest = i;
  if (list[weakest].days <= 1) list[weakest] = { id: other, days: 1 };
}

/** Everybody forgets a little, every day, worked or not. */
export function fadeBonds(people: readonly PersonState[]): void {
  for (const p of people) {
    if (!p.bonds || p.bonds.length === 0) continue;
    let write = 0;
    for (let i = 0; i < p.bonds.length; i++) {
      const d = p.bonds[i].days * BOND_FADE;
      if (d < BOND_FLOOR) continue;             // dropped
      p.bonds[write++] = { id: p.bonds[i].id, days: d };
    }
    p.bonds.length = write;
    if (write === 0) delete p.bonds;
  }
}

/** Who this person keeps company with, strongest first. Readers only. */
export const companionsOf = (p: PersonState): ReadonlyArray<{ id: string; days: number }> =>
  [...(p.bonds ?? [])].sort((a, b) => (b.days - a.days) || (a.id < b.id ? -1 : 1));

// ---------------------------------------------------------------------------
// where you keep going — the ground a life works
// ---------------------------------------------------------------------------

/** How many grounds one person can be said to keep going back to. */
const HAUNT_CAP = 3;
/** Per-day forgetting, the bonds' own rate: a ground left alone halves in
 *  ~1.9 world-years, so a season's work outlasts a winter and one errand
 *  does not outlast the month. */
const HAUNT_FADE = 0.999;
const HAUNT_FLOOR = 0.5;
/** Days on one ground before the card will say so — `BOND_SPEAKS`'s own bar. */
export const HAUNT_SPEAKS = 24;

/**
 * A day's work, recorded as ground. Called once per absolute day boundary
 * per party with its members, and the same gate `shareADay` uses: an errand
 * that asks nothing (camp, fleeing, travelling) is not going somewhere to
 * work, so it marks no ground.
 */
export function workADay(party: readonly PersonState[], activity: BandActivity, cell: string): void {
  if (asked(activity).length === 0) return;
  for (const p of party) {
    const list = p.haunts ?? (p.haunts = []);
    const at = list.findIndex((h) => h.cell === cell);
    if (at >= 0) { list[at].days += 1; continue; }
    if (list.length < HAUNT_CAP) { list.push({ cell, days: 1 }); continue; }
    let weakest = 0;
    for (let i = 1; i < list.length; i++) if (list[i].days < list[weakest].days) weakest = i;
    if (list[weakest].days <= 1) list[weakest] = { cell, days: 1 };
  }
}

/** Everybody's grounds fade a little, every day, worked or not. */
export function fadeHaunts(people: readonly PersonState[]): void {
  for (const p of people) {
    if (!p.haunts || p.haunts.length === 0) continue;
    let write = 0;
    for (let i = 0; i < p.haunts.length; i++) {
      const d = p.haunts[i].days * HAUNT_FADE;
      if (d < HAUNT_FLOOR) continue;
      p.haunts[write++] = { cell: p.haunts[i].cell, days: d };
    }
    p.haunts.length = write;
    if (write === 0) delete p.haunts;
  }
}

/**
 * The ground this person keeps going back to, or nothing: the strongest
 * haunt, and only when it has had `HAUNT_SPEAKS` days — and when it stands
 * clear of the rest, by `companionOf`'s own rule, so somebody who works
 * three grounds evenly is not said to keep going back to one of them.
 */
export function hauntOf(p: PersonState): { cell: string; days: number } | undefined {
  const h = [...(p.haunts ?? [])].sort((a, b) => (b.days - a.days) || (a.cell < b.cell ? -1 : 1));
  if (h.length === 0 || h[0].days < HAUNT_SPEAKS) return undefined;
  if (h.length === 1) return h[0];
  const mid = h[Math.floor(h.length / 2)].days;
  return mid <= 0 || h[0].days / mid >= BOND_STANDS_OUT ? h[0] : undefined;
}

// ---------------------------------------------------------------------------
// becoming — an axis crossing the line the card speaks at
// ---------------------------------------------------------------------------

/** Every axis of every person, in roster order, for `crossingsSince`. */
export function axesOf(people: readonly PersonState[]): Float64Array {
  const out = new Float64Array(people.length * TRAIT_AXES.length);
  for (let i = 0; i < people.length; i++) {
    for (let k = 0; k < TRAIT_AXES.length; k++) out[i * TRAIT_AXES.length + k] = axisOf(people[i], TRAIT_AXES[k]);
  }
  return out;
}

/** One crossing: whose, which axis, and which way. */
export interface Crossing { person: PersonState; axis: TraitAxis; up: boolean }

/**
 * Which axes crossed a card line since `before` was taken — and the record of
 * it written onto the person (`became`). The lines are crossed in both
 * directions and both are a becoming: a life that stops being bold is as much
 * a change as one that starts. One entry per axis is kept, the latest; the
 * card says the most recent.
 *
 * The roster must be the same one `axesOf` read, in the same order, with the
 * same length — a birth or a death between the two reads would misalign
 * every person after it, which is why both callers take the snapshot
 * immediately before the write they bracket and nothing else.
 */
export function crossingsSince(
  people: readonly PersonState[], before: Float64Array, now: number,
): Crossing[] {
  const out: Crossing[] = [];
  const n = TRAIT_AXES.length;
  if (before.length !== people.length * n) return out;
  for (let i = 0; i < people.length; i++) {
    const p = people[i];
    for (let k = 0; k < n; k++) {
      const was = before[i * n + k], is = axisOf(p, TRAIT_AXES[k]);
      const up = was < STANDS_OUT && is >= STANDS_OUT;
      const down = was > STANDS_APART && is <= STANDS_APART;
      if (!up && !down) continue;
      const axis = TRAIT_AXES[k];
      const list = p.became ?? (p.became = []);
      const at = list.findIndex((b) => b.axis === axis);
      if (at >= 0) list.splice(at, 1);
      list.push({ axis, up, at: now });
      out.push({ person: p, axis, up });
    }
  }
  return out;
}

/**
 * HOW THIS PERSON STANDS AGAINST THEIR OWN BAND — the one axis on which they
 * are furthest from the people around them, when they are far enough for a
 * neighbour to have noticed. Readers only.
 *
 * The card's absolute words (`STANDS_OUT` / `STANDS_APART`) are true and,
 * measured on the regrown century, shared: "bold · a hunter's hands" is on a
 * sixth of the island, and two hunters in one camp read alike. What a
 * neighbour actually says is relative — *bolder than most here*, *ranges
 * further than most* — and that is `PLAYABLE.md` P2's own example. The line
 * is spoken only when the person stands `STANDS_CLEAR` standard deviations
 * from the roster's middle on the axis they stand furthest on; measured on
 * the shipped century that is **half the island** (258 of 517), spread over
 * every axis in both directions, and the other half are honestly ordinary
 * among their own. The same discipline as `companionOf`: say it when it
 * means something, and otherwise say nothing.
 */
export const STANDS_CLEAR = 1.6;
export function standsOutIn(
  p: PersonState, people: readonly PersonState[],
): { axis: TraitAxis; up: boolean } | undefined {
  if (people.length < 4) return undefined;
  let best: TraitAxis | undefined, bz = 0;
  for (const k of TRAIT_AXES) {
    let m = 0;
    for (const q of people) m += axisOf(q, k);
    m /= people.length;
    let v = 0;
    for (const q of people) v += (axisOf(q, k) - m) ** 2;
    const sd = Math.sqrt(v / people.length);
    if (sd < 1e-6) continue;
    const z = (axisOf(p, k) - m) / sd;
    if (Math.abs(z) > Math.abs(bz)) { bz = z; best = k; }
  }
  return best !== undefined && Math.abs(bz) >= STANDS_CLEAR ? { axis: best, up: bz > 0 } : undefined;
}

/** What this person has become, most recent first. Readers only. */
export const becameOf = (p: PersonState): ReadonlyArray<{ axis: TraitAxis; up: boolean; at: number }> =>
  [...(p.became ?? [])].sort((a, b) => (b.at - a.at) || (a.axis < b.axis ? -1 : 1));

/**
 * How long a becoming stays a becoming rather than a fact, in world-seconds:
 * a decade, long enough for a child who grew bold to be a bold adult and
 * short enough that *has grown bold* is still true. The card (`ui/lives.ts`)
 * and the press (`press`, for a god who reaches becomings) read the same one.
 */
export const BECOMING_RECENT = 10 * 365 * 86400;

// ---------------------------------------------------------------------------
// what a death does to the people tied to it
// ---------------------------------------------------------------------------

/** How many losses one person carries by name. The card has room for two. */
const LOST_CAP = 6;
/**
 * How much harder the tied hold the memory of a death than a neighbour does.
 * A communal death is remembered at `salience × 1.15` (GRIM) by whoever was
 * at the hearth; a parent, a child or a companion holds it at this much more,
 * which at an elder's 0.62 reaches the ceiling — the top of THEIR card for as
 * long as nothing of the same weight happens. Measured against `hold`'s
 * ninety-day half-life: against ordinary news at 0.7 it stays the first thing
 * they carry for ~46 days, where an untied witness's copy is overtaken at
 * once. A design number written down to be measured against.
 */
export const GRIEF = 1.6;

/**
 * The people who lose somebody when this person dies, each with what they
 * were to them — parent, child, or THE companion the card would have named.
 *
 * The companion gate is `companionOf`, not a floor on days. After two years a
 * band's people hold bonds past `BOND_SPEAKS` with nearly everybody — the
 * attendance register `tools/bonds.ts` §3 measured — and a rule that let any
 * of those grieve would have the whole camp bereaved by every death, which is
 * the card lying in the other direction. Readers only.
 */
export function bereavedBy(
  gone: PersonState, people: readonly PersonState[],
): Array<{ person: PersonState; tie: 'parent' | 'child' | 'companion' }> {
  const out: Array<{ person: PersonState; tie: 'parent' | 'child' | 'companion' }> = [];
  for (const p of people) {
    if (p === gone) continue;
    if (gone.parentId === p.id) out.push({ person: p, tie: 'child' });
    else if (p.parentId === gone.id) out.push({ person: p, tie: 'parent' });
    else if (companionOf(p)?.id === gone.id) out.push({ person: p, tie: 'companion' });
  }
  return out;
}

/** One more loss, by name; the oldest forgotten past the cap. */
export function mourn(
  p: PersonState, gone: PersonState, tie: 'parent' | 'child' | 'companion', now: number,
): void {
  const list = p.lost ?? (p.lost = []);
  if (list.some((l) => l.id === gone.id)) return;
  list.push({ id: gone.id, name: gone.name, at: now, tie });
  while (list.length > LOST_CAP) list.shift();
}

/** What this person has lost, most recent first. Readers only. */
export const lossesOf = (p: PersonState): ReadonlyArray<{ id: string; name: string; at: number; tie: 'parent' | 'child' | 'companion' }> =>
  [...(p.lost ?? [])].sort((a, b) => (b.at - a.at) || (a.id < b.id ? -1 : 1));

// ---------------------------------------------------------------------------
// what a god can press on — Strengthen, applied to a life
// ---------------------------------------------------------------------------

/**
 * HOW MANY DAYS OF THEIR OWN WORK ONE CAST IS WORTH. **MEASURED**,
 * `tools/press.ts`, against the shipped century rather than a grown world.
 *
 * The unit is law 28 made literal: a verb does not add a percentage, it
 * **compresses causality** — it forces a process already running through more
 * of its own work now. So a press is priced in the only currency this system
 * has, days of the life being lived, and it reuses `HARDEN` rather than
 * introducing a second rate. One world-year is the legible reading: *a god's
 * touch is worth a year of your own living.*
 *
 * **THE BINDING FACTOR IS NOT THIS NUMBER, AND FINDING THAT OUT CHANGED IT.**
 * Decomposing the delta on the shipped century (`tools/press.ts` §1):
 *
 *     headroom (LIFE_MAX − v)   p10 0.008   p50 0.055   p90 0.249
 *     agePlasticity             p10 0.162   p50 0.385   p90 0.960
 *     asked weight w            p10 0.100   p50 0.700   p90 0.800
 *
 * Headroom dominates by an order of magnitude: **the median person on a hunt
 * party is already at `hunt ≈ 0.915`**, because `liveADay` hardens an asked
 * axis toward `LIFE_MAX` every working day and `RELAX` only ever touches axes
 * nobody asked for. After a century the veterans are pinned near the ceiling,
 * so no value of this constant can move them and inflating it would only
 * over-move the young. See the note on `HARDEN` about what that means for the
 * life model itself — it is a real finding and it is not this function's to
 * fix.
 *
 * 365 is therefore chosen against the population that HAS room: at p90
 * headroom and p50 age it moves a working axis by ~0.024 — a real shove, some
 * way short of a transformation, and one that decays on repetition because the
 * headroom factor shrinks with every press (law 25, with no anti-spam branch).
 */
export const PRESS_DAYS = 365;

/**
 * A god leans on what a person is ALREADY DOING.
 *
 * This is `DECISIONS.md`'s ruling that Strengthen absorbs Imprint, and it is
 * one function rather than a verb of its own. The rule it keeps is the whole
 * point of the design:
 *
 * > **You cannot strengthen a tendency that is not being expressed.**
 *
 * The axes it may move are exactly `asked(activity)` — the same single mapping
 * `suitability` uses to choose who goes and `liveADay` uses to decide what the
 * day made of them. So a god pressing on somebody out foraging deepens forage
 * and a little curiosity, and a god pressing on somebody sitting in camp moves
 * **nothing at all**, because camp asks for nothing. That is law 21's
 * `processViability` arriving at a person: the cast is scaled by whether the
 * process is happening, and here "not happening" is honestly zero.
 *
 * Three things it must not do, each of which would be a real defect:
 *
 *  · **It never writes `shown`.** `types.ts` says so at the field: the
 *    expression counter is written only by living. That is what lets the card
 *    say *keeps turning up on the scouting parties* and never be made to lie
 *    by a god who simply wished it. A pressed person is genuinely changed and
 *    has genuinely not done the work, and the card must keep telling those
 *    apart.
 *  · **It never reads another person.** No roster norm, no neighbour — so the
 *    result cannot depend on iteration order, which is law 7 at the one place
 *    a mid-day write could have broken it. `liveADay` earns order-independence
 *    by taking `rosterNorm` before any write; this earns it by needing nothing.
 *  · **It runs through `plasticityOf`**, so a god shapes the young and the
 *    inexperienced far more than an elder — the same wall a life meets.
 *    Repetition on one person therefore decays toward nothing on its own,
 *    which is law 25 without a special case.
 *
 * `sign` is +1 for Strengthen and −1 for Weaken, and neither is moral (law
 * 22): the mirror moves toward `LIFE_MIN` exactly as the other moves toward
 * `LIFE_MAX`. Returns the largest |Δ| it wrote, so a caller can tell "nothing
 * was being expressed" from "the press landed" without inspecting the person.
 */
export function press(
  p: PersonState,
  activity: BandActivity | undefined,
  sign: 1 | -1,
  strength: number,
  now: number,
  /**
   * THE ONE BECOME MOMENT (`PLAYABLE.md` P5; `web.ts` MORTALS_FIRST). For a
   * god who has bought *Took to It*, the press also reaches what the person
   * has LATELY BECOME — their most recent crossing of a card line — at
   * `BECOMING_WEIGHT`, in the direction they became it: Strengthen deepens a
   * becoming, Weaken undoes it. A change to the rule of the verb, not a
   * percentage: somebody who lately grew bold can be leaned on for their
   * nerve even by the fire, where the day asks nothing of it. Without it,
   * or for a person who has not become anything lately, nothing changes.
   */
  reachBecoming = false,
): number {
  if (strength <= 0) return 0;
  const rows = activity ? asked(activity) : [];

  // what this work asks of anybody, by axis — the same fold liveADay does
  const want = new Map<TraitAxis, number>();
  for (const [k, w] of rows) want.set(k, (want.get(k) ?? 0) + w);
  // …and what they have lately become, for a god whose Godhood reaches it.
  // A separate map because its direction is the becoming's own, not the
  // verb's: reinforcing a life that grew wary is a press toward wary.
  const became = new Map<TraitAxis, 1 | -1>();
  if (reachBecoming) {
    const latest = becameOf(p)[0];
    if (latest && now - latest.at <= BECOMING_RECENT && !want.has(latest.axis)) {
      became.set(latest.axis, latest.up ? 1 : -1);
    }
  }
  if (want.size === 0 && became.size === 0) return 0;

  let moved = 0;
  // TRAIT_AXES rather than the map, so the write order is the fixed one
  for (const k of TRAIT_AXES) {
    const w = want.get(k) ?? (became.has(k) ? BECOMING_WEIGHT : undefined);
    if (w === undefined || w <= 0) continue;
    const dir = (became.get(k) ?? 1) * sign;
    const toward = dir === 1 ? LIFE_MAX : LIFE_MIN;
    const v = axisOf(p, k);
    // agePlasticity, NOT plasticityOf — see that function for the measurement
    // that forced the split. A god presses against age, not against expertise.
    const d = (toward - v) * HARDEN * w * agePlasticity(p, now) * PRESS_DAYS * strength;
    setAxis(p, k, v + d);
    moved = Math.max(moved, Math.abs(axisOf(p, k) - v));
  }
  return moved;
}

/**
 * How hard a press leans on a becoming, against the work's own weights
 * (0.1–0.9 in `WORK`). Half: a becoming is a real thing to reinforce and
 * not the thing the day is asking for. A design number written down to be
 * measured against — `tools/press.ts` is the instrument.
 */
export const BECOMING_WEIGHT = 0.5;

// ---------------------------------------------------------------------------
// inheritance — DESIGN-VI §4.1, and the defect it fixes
// ---------------------------------------------------------------------------

// `TOWARD_NORM` (how far a child regresses toward the band's middle — GUESSED
// at 0.25 until `tools/saturation.ts` measured it) and `VARIATION` (the
// half-width every birth adds, without which a people converges on one
// temperament within a few generations) live on `LIFE_MODEL` now.

/**
 * Who a newborn takes after.
 *
 * `DESIGN-VI.md` §4.1: one parent, not two, and no sex — people carry none,
 * pairing is not modelled, and *the design does not want a genetic simulator*
 * (III §66). Eligibility is being alive and of an age to have a child, which
 * `tools/imprint-life.ts §5` measured as a pool of **p50 13, and never once
 * empty in twenty births** — so the rule needs no fallback, and gets one
 * anyway, because a band reduced to children and elders is a world state and
 * not an error.
 *
 * Deterministic in (band, roster index, day): the same world gives the same
 * child the same parent on every machine and after every reload.
 */
export function chooseParent(
  people: readonly PersonState[], bandSeed: number, idx: number, now: number,
): PersonState | undefined {
  const eligible: PersonState[] = [];
  for (const p of people) {
    const age = (now - p.bornAt) / LIFE_YEAR;
    if (age >= 15 && age < 45) eligible.push(p);
  }
  if (eligible.length === 0) return undefined;
  const day = Math.floor(now / 86400);
  const u = hash01(hash3(bandSeed, idx, 0x9111), day, 0x9112);
  return eligible[Math.min(eligible.length - 1, Math.floor(u * eligible.length))];
}

/**
 * Make one person. `idx` is their index within the band's whole history, so
 * the tenth person a band ever has is always the same person.
 *
 * With a `parent` and the roster's `norm`, the child is that parent's
 * temperament pulled a quarter of the way toward what is ordinary here, plus
 * variation — mid-parent regression, honestly, and the only reason a divine
 * Imprint can outlive the person it was spent on. Without them (a founder, or
 * a band with nobody of an age) it is the hash it always was.
 */
export function makeChild(
  bandId: string, bandSeed: number, idx: number, bornAt: number,
  parent: PersonState, norm: Record<TraitAxis, number>,
  model: LifeModel = LIFE_MODEL,
): PersonState {
  const p = makePerson(bandId, bandSeed, idx, bornAt);
  for (let i = 0; i < TRAIT_AXES.length; i++) {
    const k = TRAIT_AXES[i];
    const from = axisOf(parent, k);
    const base = from + (norm[k] - from) * model.towardNorm;
    const jitter = (hash01(hash3(bandSeed, idx, 0x9120 + i), i, 0x9121) - 0.5) * 2 * model.variation;
    setAxis(p, k, base + jitter);
  }
  recordBorn(p);
  p.parentId = parent.id;
  return p;
}
